import type { AmenityBarrierRow, BarrierMatch, ParcelPreview, ReportBundle } from "@/lib/parcelq/types";
import type {
  Amenity_Row,
  Barrier_Flag,
  Source_Row,
  Table_Row,
  Tax_History_Row,
  Verification_Status,
} from "@/components/parcelq/types";

function Value_Is_Empty(Value: unknown): boolean {
  return Value === null || Value === undefined || Value === "";
}

function Value_Fmt(Value: unknown): string {
  if (Value_Is_Empty(Value)) return "—";
  if (typeof Value === "boolean") return Value ? "Yes" : "No";
  if (typeof Value === "number") {
    return Number.isInteger(Value) ? Value.toLocaleString() : Value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  const Text = String(Value).trim();
  return Text || "—";
}

function Value_Fmt_Usd(Value: unknown): string {
  if (Value_Is_Empty(Value)) return "—";
  const Number_Value = Number(Value);
  if (!Number.isFinite(Number_Value)) return Value_Fmt(Value);
  return `$${Math.round(Number_Value).toLocaleString()}`;
}

function Value_Fmt_Year(Value: unknown): string {
  if (Value_Is_Empty(Value)) return "—";
  const Year = Number(Value);
  if (Number.isFinite(Year) && Year >= 1700 && Year <= 2100) return String(Math.trunc(Year));
  return Value_Fmt(Value);
}

function Row_Push(
  Rows: Table_Row[],
  Label: string,
  Value: unknown,
  Source: string,
  Source_Url?: string | null,
  Options?: {
    Allow_Empty?: boolean;
    Format?: "text" | "usd" | "year";
    Hint?: string;
    Link_Primary?: boolean;
  },
): void {
  if (!Options?.Allow_Empty && Value_Is_Empty(Value)) return;
  let Formatted = Value_Fmt(Value);
  if (Options?.Format === "usd") Formatted = Value_Fmt_Usd(Value);
  if (Options?.Format === "year") Formatted = Value_Fmt_Year(Value);
  if (!Options?.Allow_Empty && (Formatted === "—" || !Formatted)) return;

  const Url = typeof Source_Url === "string" && Source_Url.trim() ? Source_Url.trim() : undefined;
  Rows.push({
    Label,
    Value: Formatted,
    Source,
    ...(Url ? { Source_Url: Url } : {}),
    ...(Options?.Hint ? { Hint: Options.Hint } : {}),
    ...(Options?.Link_Primary && Url ? { Link_Primary: true } : {}),
  });
}

function Sources_Appendix_Find(
  Booklet: Record<string, unknown>,
  Needle: string,
): string | undefined {
  const Appendix = Booklet.sources_appendix;
  if (!Array.isArray(Appendix)) return undefined;
  const Needle_Lower = Needle.toLowerCase();
  for (const Entry of Appendix) {
    if (!Entry || typeof Entry !== "object") continue;
    const Row = Entry as Record<string, unknown>;
    const Step = String(Row.step || "").toLowerCase();
    const Dataset = String(Row.dataset || "").toLowerCase();
    const Url = String(Row.source_url || "").trim();
    if (!Url) continue;
    if (Step.includes(Needle_Lower) || Dataset.includes(Needle_Lower)) return Url;
  }
  return undefined;
}

function Url_First(...Candidates: Array<unknown>): string | undefined {
  for (const Candidate of Candidates) {
    if (typeof Candidate === "string" && Candidate.trim()) return Candidate.trim();
  }
  return undefined;
}

function List_Join(Value: unknown, Limit = 8): string | undefined {
  if (!Array.isArray(Value) || Value.length === 0) return undefined;
  const Parts = Value.map((Item) => {
    if (typeof Item === "string") return Item.trim();
    if (Item && typeof Item === "object") {
      const Record_Item = Item as Record<string, unknown>;
      return String(Record_Item.label || Record_Item.name || Record_Item.use || "").trim();
    }
    return String(Item || "").trim();
  }).filter(Boolean);
  if (!Parts.length) return undefined;
  const Shown = Parts.slice(0, Limit);
  const Extra = Parts.length > Limit ? ` (+${Parts.length - Limit} more)` : "";
  return `${Shown.join("; ")}${Extra}`;
}

function Rows_From_Object(
  Record: Record<string, unknown> | undefined,
  Source: string,
  Source_Url?: string,
  Label_Format?: (Key: string) => string,
): Table_Row[] {
  if (!Record) return [];
  const Rows: Table_Row[] = [];
  for (const [Key, Value] of Object.entries(Record)) {
    Row_Push(
      Rows,
      Label_Format ? Label_Format(Key) : Key.replace(/_/g, " "),
      typeof Value === "object" ? JSON.stringify(Value) : Value,
      Source,
      Source_Url,
    );
  }
  return Rows;
}

function Preview_Attribute_Rows(
  Block: { attributes: Record<string, unknown> } | undefined,
  Source: string,
): Table_Row[] {
  if (!Block?.attributes) return [];
  const Rows: Table_Row[] = [];
  for (const [Key, Value] of Object.entries(Block.attributes)
    .filter(([, Entry]) => !Value_Is_Empty(Entry) && String(Entry).trim() !== "")
    .slice(0, 8)) {
    Row_Push(Rows, Key.replace(/_/g, " "), Value, Source);
  }
  return Rows;
}

export function ParcelPreview_To_Zoning_Rows(Parcel: ParcelPreview): Table_Row[] {
  const Rows = Preview_Attribute_Rows(Parcel.zoning, "ArcGIS zoning");
  if (Rows.length > 0) return Rows;
  return [{ Label: "Status", Value: "Run pipeline for full zoning tables", Source: "Preview" }];
}

export function ParcelPreview_To_Hazard_Rows(Parcel: ParcelPreview): Table_Row[] {
  const Attrs = Parcel.flood?.attributes;
  if (Attrs && Object.keys(Attrs).length) {
    return [
      {
        Label: "Flood zone",
        Value: Flood_Zone_Is_Sfha(Attrs) ? "true" : "false",
        Source: "FEMA NFHL",
      },
    ];
  }
  return [{ Label: "Status", Value: "Run pipeline for hazard layers", Source: "Preview" }];
}

export function Booklet_To_Parcel_Rows(Booklet: Record<string, unknown>): Table_Row[] {
  const Parcel = Booklet.parcel as Record<string, unknown> | undefined;
  if (!Parcel) return [{ Label: "Status", Value: "No parcel data in booklet", Source: "Pipeline" }];

  const Geometry = (Parcel.geometry as Record<string, unknown> | undefined) ?? {};
  const Owner = (Parcel.recorded_owner as Record<string, unknown> | undefined) ?? {};
  const Assessor_Url = Url_First(
    Parcel.assessor_portal_url,
    Sources_Appendix_Find(Booklet, "county_assessor"),
    Geometry.source_url,
  );
  const Geometry_Url = Url_First(
    Geometry.source_url,
    Sources_Appendix_Find(Booklet, "statewide"),
    Sources_Appendix_Find(Booklet, "ccmap"),
    Assessor_Url,
  );
  const Recorder_Url = Url_First(
    (Booklet.recorded_docs_index as Record<string, unknown> | undefined)?.recorder_search_url,
    Sources_Appendix_Find(Booklet, "contra_costa_property_docs"),
    Sources_Appendix_Find(Booklet, "recorder"),
  );

  const Rows: Table_Row[] = [];
  Row_Push(Rows, "Site address", Parcel.site_address || Parcel.address, "County Assessor / GIS", Assessor_Url || Geometry_Url);
  Row_Push(Rows, "APN", Parcel.apn, "County Assessor / GIS", Assessor_Url || Geometry_Url);
  Row_Push(Rows, "City / County", [Parcel.city, Parcel.county].filter(Boolean).join(", ") || undefined, "County Assessor / GIS", Assessor_Url);
  Row_Push(Rows, "Legal description", Parcel.legal_description, "County Assessor", Assessor_Url);
  if (!Value_Is_Empty(Parcel.lot_size_sqft) || !Value_Is_Empty(Parcel.lot_size_acres)) {
    const Lot = [
      !Value_Is_Empty(Parcel.lot_size_sqft) ? `${Value_Fmt(Parcel.lot_size_sqft)} sq ft` : null,
      !Value_Is_Empty(Parcel.lot_size_acres) ? `${Value_Fmt(Parcel.lot_size_acres)} ac` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    Row_Push(Rows, "Lot size", Lot, "County Assessor / GIS", Geometry_Url || Assessor_Url);
  }
  Row_Push(Rows, "Year built", Parcel.year_built, "County Assessor", Assessor_Url, { Format: "year" });
  Row_Push(Rows, "Building area", Parcel.building_area_sqft, "County Assessor", Assessor_Url);
  Row_Push(Rows, "Use code", Parcel.use_code, "County Assessor", Assessor_Url);
  Row_Push(Rows, "Use description", Parcel.use_code_description, "County Assessor", Assessor_Url);
  Row_Push(Rows, "Tax rate area", Parcel.tax_rate_area, "County Assessor", Assessor_Url);
  Row_Push(Rows, "Boundary available", Geometry.boundary_available, "GIS parcel geometry", Geometry_Url);
  if (!Value_Is_Empty(Owner.name)) {
    const Doc_Bits = [Owner.doc_number ? `doc ${Owner.doc_number}` : null, Owner.date_recorded || null]
      .filter(Boolean)
      .join(", ");
    Row_Push(
      Rows,
      "Recorded owner",
      Doc_Bits ? `${Owner.name} (${Doc_Bits})` : Owner.name,
      "Recorder / Assessor",
      Recorder_Url || Assessor_Url,
    );
  }
  Row_Push(Rows, "Owner entity type", Parcel.owner_entity_type, "Recorder", Recorder_Url);
  Row_Push(Rows, "Ownership tenure", Parcel.ownership_tenure_type, "Recorder", Recorder_Url);
  Row_Push(Rows, "Council district", Parcel.council_district, "Local GIS", Geometry_Url);
  Row_Push(Rows, "Garbage day", Parcel.garbage_day, "Local GIS", Geometry_Url);
  if (Assessor_Url) {
    Row_Push(Rows, "Assessor portal", "Open county assessor", "County Assessor", Assessor_Url, {
      Allow_Empty: true,
    });
  }

  return Rows.length ? Rows : [{ Label: "Status", Value: "No parcel fields populated", Source: "Pipeline" }];
}

export function Booklet_To_Assessment_Rows(Booklet: Record<string, unknown>): Table_Row[] {
  const Assessment = Booklet.assessment as Record<string, unknown> | undefined;
  if (!Assessment) {
    return [{ Label: "Status", Value: "No assessment data in booklet", Source: "Pipeline" }];
  }

  const Tax_Url = Url_First(
    Assessment.source_url,
    Sources_Appendix_Find(Booklet, "tax_portal"),
    Sources_Appendix_Find(Booklet, "tax"),
  );
  const Assessor_Url = Url_First(
    (Booklet.parcel as Record<string, unknown> | undefined)?.assessor_portal_url,
    Sources_Appendix_Find(Booklet, "county_assessor"),
  );
  const History = Array.isArray(Assessment.tax_history) ? Assessment.tax_history : [];
  const From_Tax = History.length > 0 || Boolean(Tax_Url && String(Tax_Url).includes("taxcolp"));
  const Source_Label = From_Tax ? "Tax Assessor (tax portal)" : "County Assessor";
  const Source_Url = From_Tax ? Tax_Url || Assessor_Url : Assessor_Url || Tax_Url;

  const Rows: Table_Row[] = [];
  Row_Push(Rows, "Assessment year", Assessment.assessment_year, Source_Label, Source_Url, { Format: "year" });
  Row_Push(Rows, "Land value", Assessment.land_value_usd, Source_Label, Source_Url, { Format: "usd" });
  Row_Push(Rows, "Improvements", Assessment.improvements_value_usd, Source_Label, Source_Url, { Format: "usd" });
  Row_Push(Rows, "Gross assessed value", Assessment.gross_value_usd, Source_Label, Source_Url, { Format: "usd" });
  Row_Push(Rows, "Net taxable value", Assessment.total_net_taxable_value_usd, Source_Label, Source_Url, {
    Format: "usd",
  });
  Row_Push(Rows, "Tax bill history rows", Assessment.tax_history_count ?? History.length, Source_Label, Source_Url);
  Row_Push(Rows, "Lookup note", Assessment.lookup_note, "County Assessor", Assessor_Url);
  if (Source_Url) {
    Row_Push(Rows, "Source portal", "Open assessment source", Source_Label, Source_Url, { Allow_Empty: true });
  }

  return Rows.length ? Rows : [{ Label: "Status", Value: "No assessment fields populated", Source: "Pipeline" }];
}

export function Booklet_To_Tax_History_Rows(Booklet: Record<string, unknown>): Tax_History_Row[] {
  const Assessment = Booklet.assessment as Record<string, unknown> | undefined;
  const History = Array.isArray(Assessment?.tax_history) ? Assessment.tax_history : [];
  return History.slice(0, 15).map((Entry) => {
    const Row = (Entry && typeof Entry === "object" ? Entry : {}) as Record<string, unknown>;
    return {
      Tax_Year: Value_Fmt(Row.tax_year),
      Installment: Value_Fmt(Row.installment),
      Ad_Valorem: Value_Fmt_Usd(Row.ad_valorem_tax_usd),
      Special: Value_Fmt_Usd(Row.special_assessments_usd),
      Total: Value_Fmt_Usd(Row.amount_usd),
      Status: Value_Fmt(Row.bill_status),
    };
  });
}

export function Booklet_To_Zoning_Rows(Booklet: Record<string, unknown>): Table_Row[] {
  const Regulatory = Booklet.regulatory_snapshot as Record<string, unknown> | undefined;
  if (!Regulatory) return [];

  const Municode_Url = Url_First(
    Regulatory.raw_section_url,
    Array.isArray(Regulatory.source_urls) ? Regulatory.source_urls[0] : undefined,
    Sources_Appendix_Find(Booklet, "municode"),
    Sources_Appendix_Find(Booklet, "zoning"),
  );
  const Gis_Url = Url_First(
    Regulatory.gis_portal_url,
    Sources_Appendix_Find(Booklet, "general plan"),
    Sources_Appendix_Find(Booklet, "zoning"),
  );
  const General_Plan = (Regulatory.general_plan as Record<string, unknown> | undefined) ?? {};

  const Rows: Table_Row[] = [];
  Row_Push(Rows, "Zoning designation", Regulatory.zoning_designation, "Open zoning code", Municode_Url || Gis_Url, {
    Hint: "Official zone code plus plain-language meaning (what you may build on this lot).",
    Link_Primary: true,
  });
  Row_Push(
    Rows,
    "General Plan",
    General_Plan.label || General_Plan.designation,
    "Open General Plan map",
    Gis_Url,
    {
      Hint: "Long-range city plan designation — may differ from today’s zoning.",
      Link_Primary: true,
    },
  );
  Row_Push(
    Rows,
    "Density (housing units per acre)",
    Regulatory.density_units_per_acre,
    "Open zoning code",
    Municode_Url,
    {
      Hint: "How many homes the zone allows per acre of land.",
      Link_Primary: true,
    },
  );
  Row_Push(Rows, "Max floor area ratio", Regulatory.far, "Open zoning code", Municode_Url, {
    Hint: "Max building floor area compared to lot size (higher = larger building).",
    Link_Primary: true,
  });
  Row_Push(Rows, "Max height (ft)", Regulatory.max_height_ft, "Open zoning code", Municode_Url, {
    Hint: "Tallest the building can be, measured in feet.",
    Link_Primary: true,
  });
  Row_Push(Rows, "Max lot coverage (%)", Regulatory.max_lot_coverage_pct, "Open zoning code", Municode_Url, {
    Hint: "Percent of the lot that buildings may cover (not open yard).",
    Link_Primary: true,
  });
  Row_Push(Rows, "Min lot area (sq ft)", Regulatory.min_lot_area_sqft, "Open zoning code", Municode_Url, {
    Hint: "Smallest lot size this zone normally allows, in square feet.",
    Link_Primary: true,
  });
  Row_Push(Rows, "Min lot width (ft)", Regulatory.min_lot_width_ft, "Open zoning code", Municode_Url, {
    Hint: "Minimum street frontage width in feet.",
    Link_Primary: true,
  });
  Row_Push(Rows, "Parking", Regulatory.parking_required, "Open zoning code", Municode_Url, {
    Hint: "How many parking spaces the code usually requires.",
    Link_Primary: true,
  });
  Row_Push(Rows, "Permitted uses", List_Join(Regulatory.permitted_uses), "Open zoning code", Municode_Url, {
    Hint: "Uses allowed by right (no special permit).",
    Link_Primary: true,
  });
  Row_Push(Rows, "Conditional uses", List_Join(Regulatory.conditional_uses), "Open zoning code", Municode_Url, {
    Hint: "Uses that need a special permit or hearing.",
    Link_Primary: true,
  });
  Row_Push(Rows, "Source note", Regulatory.source_note, "Open zoning code", Municode_Url, {
    Link_Primary: true,
  });

  const Overlays = Regulatory.overlays as Array<{ label?: string; detail?: string }> | undefined;
  if (Overlays?.length) {
    for (const Overlay of Overlays.slice(0, 8)) {
      Row_Push(Rows, Overlay.label || "Overlay", Overlay.detail || "Mapped", "Open map layer", Gis_Url, {
        Hint: "Extra rule layer on top of base zoning.",
        Link_Primary: true,
      });
    }
  }

  return Rows;
}

export function Booklet_To_Setback_Rows(Booklet: Record<string, unknown>): Table_Row[] {
  const Regulatory = Booklet.regulatory_snapshot as Record<string, unknown> | undefined;
  const Setbacks = Regulatory?.setbacks_ft as Record<string, unknown> | undefined;
  const Municode_Url = Url_First(
    Regulatory?.raw_section_url,
    Array.isArray(Regulatory?.source_urls) ? (Regulatory?.source_urls as unknown[])[0] : undefined,
    Sources_Appendix_Find(Booklet, "municode"),
  );
  const Hint_By_Key: Record<string, string> = {
    front: "Required open distance from the front property line to the building, in feet.",
    side: "Required open distance from a side property line to the building, in feet.",
    rear: "Required open distance from the rear property line to the building, in feet.",
    street_side: "Required open distance from a street-side property line, in feet.",
  };
  if (!Setbacks) {
    return [{ Label: "Status", Value: "No setback data in booklet", Source: "Pipeline" }];
  }
  const Rows: Table_Row[] = [];
  for (const [Key, Value] of Object.entries(Setbacks)) {
    const Label_Base = Key.replace(/_/g, " ");
    Row_Push(
      Rows,
      `${Label_Base} setback (ft)`,
      Value,
      "Open zoning code",
      Municode_Url,
      {
        Hint: Hint_By_Key[Key] || "Required distance from the property line to the building, in feet.",
        Link_Primary: true,
      },
    );
  }
  return Rows.length > 0
    ? Rows
    : [{ Label: "Status", Value: "No setback data in booklet", Source: "Pipeline" }];
}

function Flood_Zone_Is_Sfha(Values: Record<string, unknown>): boolean {
  if (typeof Values.sfha === "boolean") return Values.sfha;
  const Sfha_Raw = String(Values.sfha ?? Values.SFHA_TF ?? "").trim().toUpperCase();
  if (Sfha_Raw === "T" || Sfha_Raw === "TRUE" || Sfha_Raw === "YES") return true;
  if (Sfha_Raw === "F" || Sfha_Raw === "FALSE" || Sfha_Raw === "NO") return false;
  const Zone = String(Values.flood_zone ?? Values.FLD_ZONE ?? "").trim().toUpperCase();
  if (!Zone || Zone === "X" || Zone === "AREA NOT INCLUDED") return false;
  return true;
}

function Hazard_Layer_Parts(Layer_Key: string, Layer: Record<string, unknown>): string[] {
  const Values = (Layer.values as Record<string, unknown> | undefined) ?? Layer;
  const Parts: string[] = [];

  if (Layer_Key === "fema_flood") {
    Parts.push(`flood zone: ${Flood_Zone_Is_Sfha(Values) ? "true" : "false"}`);
    if (!Value_Is_Empty(Values.subtype)) {
      Parts.push(`subtype: ${Value_Fmt(Values.subtype)}`);
    }
    if (!Value_Is_Empty(Values.note) && Values.unavailable) {
      Parts.push(`note: ${Value_Fmt(Values.note)}`);
    }
    return Parts;
  }

  for (const Key of [
    "flood_zone",
    "zone",
    "subtype",
    "sfha",
    "hazard",
    "designation",
    "note",
    "in_ap_zone",
    "in_liquefaction_zone",
    "in_coastal_zone",
    "present",
    "sites_in_query_count",
  ]) {
    if (!Value_Is_Empty(Values[Key])) {
      const Label = Key.replace(/_/g, " ");
      Parts.push(`${Label}: ${Value_Fmt(Values[Key])}`);
    }
  }
  if (!Parts.length && !Value_Is_Empty(Layer.status)) {
    Parts.push(Value_Fmt(Layer.status));
  }

  const On_Parcel = Layer.on_parcel_matches;
  if (Array.isArray(On_Parcel) && On_Parcel.length) {
    Parts.push(`${On_Parcel.length} on-parcel match(es)`);
  }

  return Parts;
}

export function Booklet_To_Hazard_Rows(Booklet: Record<string, unknown>): Table_Row[] {
  const Hazards = Booklet.hazard_layers as Record<string, unknown> | undefined;
  const Docs = Booklet.recorded_docs_index as Record<string, unknown> | undefined;
  const Rows: Table_Row[] = [];

  if (Hazards) {
    for (const [Layer_Key, Layer_Value] of Object.entries(Hazards)) {
      if (!Layer_Value || typeof Layer_Value !== "object") continue;
      const Layer = Layer_Value as Record<string, unknown>;
      const Source_Url = Url_First(Layer.source_url, Sources_Appendix_Find(Booklet, Layer_Key));
      const Parts = Hazard_Layer_Parts(Layer_Key, Layer);

      if (Parts.length) {
        Row_Push(Rows, Layer_Key.replace(/_/g, " "), Parts.join(" · "), "Hazard layer", Source_Url);
      }
    }
  }

  const Phase = Docs?.phase_esa_reports as Record<string, unknown> | undefined;
  if (Phase) {
    const Recorder_Url = Url_First(Docs?.recorder_search_url, Sources_Appendix_Find(Booklet, "recorder"));
    Row_Push(Rows, "Phase I ESA recorded", Phase.phase_i_recorded, "Recorder index", Recorder_Url);
    Row_Push(Rows, "Phase II ESA recorded", Phase.phase_ii_recorded, "Recorder index", Recorder_Url);
  }

  return Rows.length > 0
    ? Rows
    : [{ Label: "Status", Value: "No hazard layers returned", Source: "Pipeline" }];
}

export function Booklet_To_Docs_Rows(Booklet: Record<string, unknown>): Table_Row[] {
  const Docs = Booklet.recorded_docs_index as Record<string, unknown> | undefined;
  if (!Docs) return [{ Label: "Status", Value: "No recorded documents index", Source: "Pipeline" }];

  const Recorder_Url = Url_First(
    Docs.recorder_search_url,
    Sources_Appendix_Find(Booklet, "contra_costa_property_docs"),
    Sources_Appendix_Find(Booklet, "recorder"),
  );
  const Rows: Table_Row[] = [];
  Row_Push(Rows, "Deed count", Docs.deed_count, "County Recorder", Recorder_Url);
  Row_Push(Rows, "Encumbrance count", Docs.encumbrance_count, "County Recorder", Recorder_Url);
  if (Recorder_Url) {
    Row_Push(Rows, "Recorder search", "Open recorder search", "County Recorder", Recorder_Url, {
      Allow_Empty: true,
    });
  }

  const Deeds = Array.isArray(Docs.deeds) ? Docs.deeds : [];
  for (const [Index, Entry] of Deeds.slice(0, 5).entries()) {
    if (!Entry || typeof Entry !== "object") continue;
    const Deed = Entry as Record<string, unknown>;
    const Value = [
      Deed.doc_type,
      Deed.doc_number ? `#${Deed.doc_number}` : null,
      Deed.recording_date,
      Deed.grantor ? `from ${Deed.grantor}` : null,
      Deed.grantee ? `to ${Deed.grantee}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    Row_Push(Rows, `Deed ${Index + 1}`, Value, "County Recorder", Recorder_Url);
  }

  const Encumbrances = Array.isArray(Docs.encumbrances) ? Docs.encumbrances : [];
  for (const [Index, Entry] of Encumbrances.slice(0, 5).entries()) {
    if (!Entry || typeof Entry !== "object") continue;
    const Enc = Entry as Record<string, unknown>;
    const Value = [
      Enc.doc_type,
      Enc.doc_number ? `#${Enc.doc_number}` : null,
      Enc.recording_date,
      Enc.parties || Enc.grantor || Enc.grantee,
    ]
      .filter(Boolean)
      .join(" · ");
    Row_Push(Rows, `Encumbrance ${Index + 1}`, Value, "County Recorder", Recorder_Url);
  }

  return Rows.length
    ? Rows
    : [{ Label: "Status", Value: "No recorded documents found", Source: "Pipeline" }];
}

export function Booklet_To_Incentive_Rows(Booklet: Record<string, unknown>): Table_Row[] {
  const Incentives = Booklet.development_incentives as Record<string, unknown> | undefined;
  if (!Incentives) return [];

  const Hud_Url = Url_First(
    Incentives.source_url,
    Sources_Appendix_Find(Booklet, "hud"),
    "https://www.huduser.gov/portal/sadda/sadda_qct.html",
  );
  const Rows: Table_Row[] = [];
  Row_Push(Rows, "Qualified Census Tract (QCT)", Incentives.qct_designated, "HUD QCT layer", Hud_Url, {
    Allow_Empty: false,
  });
  const Dda_Value =
    Incentives.dda_designated === true && Incentives.dda_name
      ? `Yes — ${Incentives.dda_name}`
      : Incentives.dda_designated;
  Row_Push(Rows, "Difficult Development Area (DDA)", Dda_Value, "HUD DDA layer", Hud_Url);
  Row_Push(Rows, "Opportunity Zone", Incentives.opportunity_zone_designated, "HUD OZ layer", Hud_Url);
  Row_Push(Rows, "Census tract GEOID", Incentives.census_tract_geoid, "FCC / HUD", Hud_Url);
  Row_Push(Rows, "Note", Incentives.note, "HUD", Hud_Url);
  return Rows;
}

function District_Names(Value: unknown, Detail?: (District: Record<string, unknown>) => string | null): string | undefined {
  if (!Array.isArray(Value) || !Value.length) return undefined;
  const Parts = Value.flatMap((Entry) => {
    if (!Entry || typeof Entry !== "object") return [];
    const District = Entry as Record<string, unknown>;
    const Name = String(District.name || "").trim();
    if (!Name) return [];
    const Extra = Detail ? Detail(District) : null;
    return [Extra ? `${Name} (${Extra})` : Name];
  });
  return Parts.length ? Parts.join("; ") : undefined;
}

export function Booklet_To_Utility_Rows(Booklet: Record<string, unknown>): Table_Row[] {
  const Utilities = Booklet.utilities_and_agencies as Record<string, unknown> | undefined;
  if (!Utilities) return [];

  const Districts_Url = Url_First(
    Utilities.source_url,
    Sources_Appendix_Find(Booklet, "districts"),
  );
  const Rows: Table_Row[] = [];
  Row_Push(Rows, "Water agency / district", District_Names(Utilities.water_districts), "DWR water districts GIS", Districts_Url);
  Row_Push(
    Rows,
    "Fire protection",
    District_Names(Utilities.fire_districts, (District) => (District.phone ? String(District.phone) : null)),
    "CAL FIRE / Cal OES districts GIS",
    Districts_Url,
  );
  Row_Push(
    Rows,
    "School district(s)",
    District_Names(Utilities.school_districts, (District) =>
      [District.district_type, District.grades ? `grades ${District.grades}` : null].filter(Boolean).join(", ") || null,
    ),
    "CDE school district GIS",
    Districts_Url,
  );
  Row_Push(Rows, "Council district", Utilities.council_district, "Local GIS", Districts_Url);
  Row_Push(Rows, "Garbage day", Utilities.garbage_day, "Local GIS", Districts_Url);
  Row_Push(Rows, "Coverage note", Utilities.coverage_note, "ParcelQ", undefined);
  Row_Push(Rows, "Capacity note", Utilities.capacity_note, "ParcelQ", undefined);
  return Rows;
}

export function Booklet_To_Agency_Rows(Booklet: Record<string, unknown>): Table_Row[] {
  const Utilities = Booklet.utilities_and_agencies as Record<string, unknown> | undefined;
  const Contacts = Array.isArray(Utilities?.agency_contacts) ? Utilities.agency_contacts : [];
  const Rows: Table_Row[] = [];
  for (const Entry of Contacts.slice(0, 12)) {
    if (!Entry || typeof Entry !== "object") continue;
    const Contact = Entry as Record<string, unknown>;
    const Agency = String(Contact.agency || "").trim();
    if (!Agency) continue;
    const Value = [
      Contact.role,
      Contact.phone,
      Contact.address || Contact.detail,
      Contact.verification === "unverified" ? "unverified" : null,
    ]
      .filter(Boolean)
      .join(" · ");
    Row_Push(Rows, Agency, Value || "—", Contact.verification === "gis_mapped" ? "District GIS" : "Directory (unverified)");
  }
  return Rows;
}

export function Booklet_To_Public_Docs_Rows(Booklet: Record<string, unknown>): Table_Row[] {
  const Docs = Array.isArray(Booklet.public_documents) ? Booklet.public_documents : [];
  const Rows: Table_Row[] = [];
  for (const Entry of Docs.slice(0, 20)) {
    if (!Entry || typeof Entry !== "object") continue;
    const Doc = Entry as Record<string, unknown>;
    const Label = String(Doc.label || "").trim();
    const Url = String(Doc.url || "").trim();
    if (!Label || !Url) continue;
    Row_Push(Rows, Label, "Open portal", String(Doc.source || "Public portal"), Url, { Allow_Empty: true });
  }
  return Rows;
}

export function Booklet_To_Sources_Rows(Booklet: Record<string, unknown>): Source_Row[] {
  const Appendix = Booklet.sources_appendix;
  if (!Array.isArray(Appendix)) return [];

  const Rows: Source_Row[] = [];
  const Seen = new Set<string>();
  for (const Entry of Appendix) {
    if (!Entry || typeof Entry !== "object") continue;
    const Row = Entry as Record<string, unknown>;
    if (String(Row.step || "") === "amenities_orchestrator") continue;
    const Source_Url = String(Row.source_url || "").trim();
    if (!Source_Url || Seen.has(Source_Url)) continue;
    Seen.add(Source_Url);
    Rows.push({
      Dataset: String(Row.dataset || Row.step || "Source"),
      Retrieved: String(Row.retrieved_at_iso || Row.retrieved_at_unix || "—"),
      Source_Url,
    });
  }
  return Rows;
}

export function Amenity_Match_Id(Match: BarrierMatch, Distance_Miles?: number): string {
  const lat = Match.lat == null ? "nil" : Match.lat.toFixed(6);
  const lon = Match.lon == null ? "nil" : Match.lon.toFixed(6);
  const dist = Distance_Miles == null ? "nil" : Distance_Miles.toFixed(6);
  return `${Match.category}::${Match.provider_name}::${lat}::${lon}::${dist}`;
}

function Barrier_Flag_Normalize(Value: string): Barrier_Flag {
  const Normalized = Value.trim().toLowerCase();
  if (Normalized === "yes") return "Yes";
  if (Normalized === "no") return "No";
  return "Unknown";
}

function Verification_Status_Normalize(Value: string | undefined): Verification_Status {
  const Normalized = (Value ?? "").trim().toLowerCase();
  if (Normalized === "present") return "Present";
  if (Normalized === "not present") return "Not present";
  if (Normalized === "present but criteria unverified") {
    return "Present but criteria unverified";
  }
  if (Normalized === "unverified") return "Unverified";
  return "Unverified";
}

export function Amenity_Name_Is_Unnamed(Name: string): boolean {
  const Normalized = Name.trim().toLowerCase();
  if (!Normalized) return true;
  if (Normalized === "(unnamed)") return true;
  return Normalized.startsWith("(unnamed");
}

function Amenity_Rows_Compare(A: Amenity_Row, B: Amenity_Row): number {
  const A_Unnamed = Amenity_Name_Is_Unnamed(A.Name);
  const B_Unnamed = Amenity_Name_Is_Unnamed(B.Name);
  if (A_Unnamed !== B_Unnamed) return A_Unnamed ? 1 : -1;
  if (A.Distance_Ft !== B.Distance_Ft) return A.Distance_Ft - B.Distance_Ft;
  return A.Name.localeCompare(B.Name, undefined, { sensitivity: "base" });
}

export function Amenity_Rows_From_Api(Amenities: AmenityBarrierRow[]): Amenity_Row[] {
  return Amenities.map((Row, Index) => {
    const Flag = Barrier_Flag_Normalize(Row.barrier_flag);
    const Id = `${Index}::${Amenity_Match_Id(Row.match, Row.distance_miles)}`;

    return {
      Id,
      Name: Row.name,
      Category: Row.category,
      Distance_Ft: Math.round((Row.distance_miles ?? 0) * 5280),
      Barrier_Flag: Flag,
      Verification_Status: Verification_Status_Normalize(Row.verification_status),
      Match: Row.match,
    };
  }).sort(Amenity_Rows_Compare);
}

export function Amenity_Rows_To_Flags(Rows: Amenity_Row[]) {
  return Rows.flatMap((Row) =>
    Row.Match ? [{ match: Row.Match, barrier_flag: Row.Barrier_Flag }] : [],
  );
}

export function Rings_To_Svg_Points(
  Rings: number[][][],
  Width: number,
  Height: number,
  Padding = 24,
): string {
  const Ring = Rings[0];
  if (!Ring?.length) return "";

  let Min_X = Infinity;
  let Max_X = -Infinity;
  let Min_Y = Infinity;
  let Max_Y = -Infinity;

  for (const [Lon, Lat] of Ring) {
    Min_X = Math.min(Min_X, Lon);
    Max_X = Math.max(Max_X, Lon);
    Min_Y = Math.min(Min_Y, Lat);
    Max_Y = Math.max(Max_Y, Lat);
  }

  const Span_X = Max_X - Min_X || 0.0001;
  const Span_Y = Max_Y - Min_Y || 0.0001;
  const Scale = Math.min((Width - Padding * 2) / Span_X, (Height - Padding * 2) / Span_Y);

  return Ring.map(([Lon, Lat]) => {
    const X = Padding + (Lon - Min_X) * Scale;
    const Y = Height - Padding - (Lat - Min_Y) * Scale;
    return `${X.toFixed(1)},${Y.toFixed(1)}`;
  }).join(" ");
}

export function Bundle_Label(Bundle: ReportBundle): string {
  switch (Bundle) {
    case "amenities":
      return "Amenity Report + Radius Map";
    case "feasibility":
      return "TTT Report";
    default:
      return "Combination Report";
  }
}

export type Red_Flag_Mapped = {
  Code: string;
  Label: string;
  Detail: string;
  Severity: string;
  Source?: string;
  Source_Url?: string;
  Reason_Resident?: string;
  Reason_Developer?: string;
};

function Tax_Year_Key(Raw: unknown): { Has: boolean; Year: number } {
  const Digits = String(Raw ?? "").replace(/\D/g, "");
  if (Digits.length >= 4) {
    const Year = Number(Digits.slice(0, 4));
    if (Number.isFinite(Year)) return { Has: true, Year };
  }
  return { Has: false, Year: 0 };
}

function Tax_Row_Unpaid(Row: Record<string, unknown>, Allow_Penalty: boolean): boolean {
  const Status = String(Row.bill_status ?? "").trim().toLowerCase();
  const Unpaid_Tokens = ["delinq", "default", "unpaid", "past due"];
  const Paid_Tokens = ["paid", "current", "cleared", "satisfied"];
  const Has_Unpaid = Unpaid_Tokens.some((Token) => Status.includes(Token));
  const Has_Paid = Paid_Tokens.some((Token) => Status.includes(Token));
  if (Has_Unpaid) return true;
  if (Has_Paid) return false;
  if (!Allow_Penalty) return false;
  const Penalty = Number(Row.delinquent_penalty_usd ?? 0);
  return Number.isFinite(Penalty) && Penalty > 0;
}

function Assessment_Tax_Delinquent(Assessment: Record<string, unknown>): boolean {
  const History = Array.isArray(Assessment.tax_history) ? Assessment.tax_history : [];
  const Rows = History.filter((Row): Row is Record<string, unknown> => !!Row && typeof Row === "object");
  if (!Rows.length) return false;
  const Years = Rows.map((Row) => Tax_Year_Key(Row.tax_year));
  if (Years.some((Y) => Y.Has)) {
    const Latest = Math.max(...Years.filter((Y) => Y.Has).map((Y) => Y.Year));
    const Latest_Rows = Rows.filter((_, Index) => Years[Index]?.Has && Years[Index]?.Year === Latest);
    return Latest_Rows.some((Row) => Tax_Row_Unpaid(Row, true));
  }
  return Rows.some((Row) => Tax_Row_Unpaid(Row, false));
}

function Hazards_Sites_Within_1mi(Booklet: Record<string, unknown>): number {
  const Hazards =
    Booklet.hazard_layers && typeof Booklet.hazard_layers === "object"
      ? (Booklet.hazard_layers as Record<string, unknown>)
      : {};
  const Brown =
    Hazards.brownfields && typeof Hazards.brownfields === "object"
      ? (Hazards.brownfields as Record<string, unknown>)
      : {};
  const Count = Number(Brown.sites_within_1mi_count ?? 0);
  return Number.isFinite(Count) ? Count : 0;
}

function Flag_Fields_From_Row(Row: Record<string, unknown>): {
  Source?: string;
  Source_Url?: string;
  Reason_Resident?: string;
  Reason_Developer?: string;
} {
  const Source = String(Row.source || "").trim();
  const Source_Url = String(Row.source_url || "").trim();
  const Reason_Resident = String(Row.reason_resident || "").trim();
  const Reason_Developer = String(Row.reason_developer || "").trim();
  return {
    ...(Source ? { Source } : {}),
    ...(Source_Url ? { Source_Url } : {}),
    ...(Reason_Resident ? { Reason_Resident } : {}),
    ...(Reason_Developer ? { Reason_Developer } : {}),
  };
}

export function Booklet_To_Red_Flags(Booklet: Record<string, unknown>): Red_Flag_Mapped[] {
  const Raw = Booklet.red_flags;
  if (!Array.isArray(Raw)) return [];
  const Assessment =
    Booklet.assessment && typeof Booklet.assessment === "object"
      ? (Booklet.assessment as Record<string, unknown>)
      : {};
  const Tax_Delinquent = Assessment_Tax_Delinquent(Assessment);
  const Sites_Within_1mi = Hazards_Sites_Within_1mi(Booklet);

  const Mapped = Raw.flatMap((Item) => {
    if (!Item || typeof Item !== "object") return [];
    const Row = Item as Record<string, unknown>;
    const Label = String(Row.label || "").trim();
    if (!Label) return [];
    const Code = String(Row.code || Label).trim();
    const Extra = Flag_Fields_From_Row(Row);

    // Nearby EnviroStor only when within 1 mile (drops old 5-mile regional noise).
    if (Code === "envirostor_nearby") {
      if (Sites_Within_1mi <= 0) return [];
      return [
        {
          Code,
          Label,
          Detail: `${Sites_Within_1mi} cleanup site(s) within 1 mile on the state EnviroStor map`,
          Severity: String(Row.severity || "medium").trim() || "medium",
          ...Extra,
        },
      ];
    }

    if (Code === "tax_delinquency") {
      if (!Tax_Delinquent) return [];
      return [
        {
          Code,
          Label: "Unpaid property taxes",
          Detail: "Latest tax year shows delinquent / unpaid indicators",
          Severity: String(Row.severity || "high").trim() || "high",
          ...Extra,
          Source: Extra.Source || "County tax / assessment records",
          Reason_Resident: Extra.Reason_Resident || "Unpaid taxes can lead to liens or sale risk.",
          Reason_Developer:
            Extra.Reason_Developer || "Tax liens and clearing costs can block closing or refinance.",
        },
      ];
    }

    return [
      {
        Code,
        Label,
        Detail: String(Row.detail || "").trim(),
        Severity: String(Row.severity || "high").trim() || "high",
        ...Extra,
      },
    ];
  });

  // If latest-year tax is delinquent but stored flags omitted it, surface it.
  if (Tax_Delinquent && !Mapped.some((Flag) => Flag.Code === "tax_delinquency")) {
    Mapped.push({
      Code: "tax_delinquency",
      Label: "Unpaid property taxes",
      Detail: "Latest tax year shows delinquent / unpaid indicators",
      Severity: "high",
      Source: "County tax / assessment records",
      Reason_Resident: "Unpaid taxes can lead to liens or sale risk.",
      Reason_Developer: "Tax liens and clearing costs can block closing or refinance.",
    });
  }

  return Mapped;
}

/** Category tokens for livability vs affordable-housing amenity views. */
const Amenity_View_Livability = [
  "grocery",
  "pharmacy",
  "park",
  "school",
  "library",
  "hospital",
  "medical",
  "community",
  "childcare",
  "transit",
  "bus",
  "rail",
  "senior",
  "shopping",
  "employment",
];

const Amenity_View_Affordable = [
  "transit",
  "bus",
  "rail",
  "park",
  "school",
  "grocery",
  "hospital",
  "medical",
  "pharmacy",
  "community_center",
  "library",
  "opportunity",
];

export function Amenity_Category_Layer(Category: string): string {
  const Lower = Category.toLowerCase();
  if (/transit|bus|rail|ferry|gtfs/.test(Lower)) return "Transit";
  if (/grocery|supermarket/.test(Lower)) return "Grocery";
  if (/pharmacy/.test(Lower)) return "Pharmacy";
  if (/hospital|medical|clinic|urgent/.test(Lower)) return "Medical";
  if (/school|college|university|education/.test(Lower)) return "Schools";
  if (/park|playground|trail|recreation/.test(Lower)) return "Parks";
  if (/library/.test(Lower)) return "Libraries";
  if (/shopping|mall|department_store|retail|employment|industrial|commercial/.test(Lower)) {
    return "Shopping & employment";
  }
  if (/city_hall|townhall|police|fire_station|post_office|social/.test(Lower)) {
    return "Community services";
  }
  if (/community|senior|childcare|population/.test(Lower)) return "Community";
  if (/opportunity|qct|dda/.test(Lower)) return "Housing resources";
  return "Other";
}

export function Amenity_Row_Matches_View(
  Category: string,
  View: "livability" | "affordable_housing" | "all",
): boolean {
  if (View === "all") return true;
  const Lower = Category.toLowerCase();
  const Tokens = View === "livability" ? Amenity_View_Livability : Amenity_View_Affordable;
  return Tokens.some((Token) => Lower.includes(Token));
}
